import { AttributeMetadata, ControlId } from '@amzn/controllershiphub-typescript-client';
import { apiCall } from 'src/utils/ApiCall';
import { ApiPaths } from 'src/common/config/ApiConstants';
import { CommentFieldType, FIELD_CONFIGS, createAuditInfo } from 'src/utils/ControlSummaryUtils';

/**
 * Props interface for the BulkEditModal component
 * @interface BulkEditModalProps
 */
export interface BulkEditModalProps {
  visible: boolean;
  onDismiss: () => void;
  selectedRows: any[];
  attributesMetadata: AttributeMetadata[] | undefined;
  controlId: ControlId | undefined;
  controlDatasetKey: string;
  onSaveSuccess?: (updatedRecords: any[]) => void;
  clearGridSelection?: () => void;
}

/**
 * Parameters for saving bulk edit changes
 * @interface SaveBulkEditParams
 */
export interface SaveBulkEditParams {
  selectedRows: any[];
  actualEditData: Record<string, any>;
  controlId: string;
  controlDatasetKey: string;
  alias: string;
}

/**
 * Saves bulk edit changes to the server
 *
 * @async
 * @param {SaveBulkEditParams} params - Parameters for the save operation
 * @param {Array<any>} params.selectedRows - Array of selected rows to update
 * @param {Record<string, any>} params.actualEditData - Object containing the edited field values
 * @param {string} params.controlId - ID of the control being edited
 * @param {string} params.controlDatasetKey - Key field for the control dataset
 * @param {string} params.alias - User alias making the changes
 * @returns {Promise<any>} Promise that resolves when the save is complete
 * @throws {Error} If required parameters are missing or save operation fails
 */
export const saveBulkEditChanges = async ({
  selectedRows,
  actualEditData,
  controlId,
  controlDatasetKey,
  alias,
}: SaveBulkEditParams) => {
  // Validate required parameters
  if (!controlId) {
    throw new Error('controlId is required');
  }
  if (!controlDatasetKey) {
    throw new Error('controlDatasetKey is required');
  }

  const controlKeyToUpdatesMap: Record<string, any> = {};

  selectedRows.forEach((row) => {
    const recordId = row[controlDatasetKey];
    if (!recordId) return;

    const updates: Array<{ name: string; value: string }> = [];

    Object.entries(actualEditData).forEach(([key, value]) => {
      if (key === controlDatasetKey) return;

      const commentConfig = FIELD_CONFIGS.find((cfg) => cfg.sourceColumn === key);
      if (commentConfig?.type === CommentFieldType.STANDARD) {
        updates.push({
          name: key,
          value: String(value || ''),
        });

        // Only add target column if it's not already being updated
        if (!actualEditData.hasOwnProperty(commentConfig.targetColumn)) {
          const auditInfo = createAuditInfo(alias || '', String(value || ''), commentConfig);
          updates.push({
            name: commentConfig.targetColumn,
            value: JSON.stringify(auditInfo),
          });
        }
      } else if (commentConfig) {
        const auditInfo = createAuditInfo(alias || '', String(value || ''));
        updates.push({
          name: key,
          value: JSON.stringify(auditInfo),
        });
      } else {
        updates.push({
          name: key,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value || ''),
        });
      }
    });

    controlKeyToUpdatesMap[String(recordId)] = updates;
  });

  const response = await apiCall<{ results: { name: string; status: string }[] }>({
    endpoint: `${ApiPaths.updateControlData}/${controlId}`,
    method: 'PATCH',
    body: { controlKeyToUpdatesMap },
  });

  if (!response.results?.every((result) => result.status === 'SUCCESS')) {
    const failedResults = response.results.filter((result) => result.status !== 'SUCCESS');
    throw new Error(`Save failed for: ${failedResults.map((r) => r.name).join(', ')}`);
  }

  return response;
};

/**
 * Gets updated records with applied changes
 *
 * @param {Omit<SaveBulkEditParams, 'controlId' | 'controlDatasetKey'>} params - Parameters for getting updated records
 * @param {Array<any>} params.selectedRows - Array of selected rows
 * @param {Record<string, any>} params.actualEditData - Object containing the edited field values
 * @param {string} params.alias - User alias making the changes
 * @returns {Array<any>} Array of updated records with changes applied
 */
export const getUpdatedRecords = ({
  selectedRows,
  actualEditData,
  alias,
}: Omit<SaveBulkEditParams, 'controlId' | 'controlDatasetKey'>) => {
  return selectedRows.map((row) => {
    const updatedRow = { ...row };

    Object.entries(actualEditData).forEach(([key, value]) => {
      const commentConfig = FIELD_CONFIGS.find((cfg) => cfg.sourceColumn === key);
      if (commentConfig?.type === CommentFieldType.STANDARD) {
        updatedRow[key] = value;
        // Only update target column if it's not already being updated
        if (!actualEditData.hasOwnProperty(commentConfig.targetColumn)) {
          const auditInfo = createAuditInfo(alias || '', String(value || ''), commentConfig);
          updatedRow[commentConfig.targetColumn] = JSON.stringify(auditInfo);
        }
      } else if (commentConfig) {
        const auditInfo = createAuditInfo(alias || '', String(value || ''));
        updatedRow[key] = JSON.stringify(auditInfo);
      } else {
        updatedRow[key] = value;
      }
    });

    return updatedRow;
  });
};
